import * as ChannelService from '@channel.io/channel-web-sdk-loader'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import styled from 'styled-components'

import { QUERY_KEY } from '../../constants'

import { abrViewSubscriptionProductList } from '../../utils/abr'

import { getProductSaleList } from '../../api/product'
import type { GetProductSaleListParams } from '../../api/product'

import { Content } from '../../components/container/Content'
import { CenterWrap } from '../../components/common'
import { Sort } from '../../components/paramOption/Sort'
import { InfinityScroll } from '../../components/pagingList/InfinityScroll'
import { Spinner } from '../../components/spinner/Spinner'
import { ProductCard } from './components'

const SORT_LIST: { type: GetProductSaleListParams['sort']; title: string }[] = [
  { type: 'createdDate,desc', title: '최근순' },
  { type: 'createdDate,asc', title: '과거순' },
]

export function MainSaleList() {
  const [sort, setSort] = useState<GetProductSaleListParams['sort']>('createdDate,desc')

  const { data, isPending, isError, hasNextPage, refetch, fetchNextPage } = useInfiniteQuery({
    queryKey: [QUERY_KEY.productSales, { sort }],
    queryFn: async ({ pageParam = 0 }) => await getProductSaleList({ offset: pageParam, size: 10, sort }),
    getNextPageParam: (lastPage) => (lastPage.last ? undefined : lastPage.offset + lastPage.size),
    initialPageParam: 0,
    select: (data) => ({
      ...data,
      pages: data.pages.map((page) => page.list).flat(),
    }),
  })

  useEffect(() => {
    abrViewSubscriptionProductList()
    ChannelService.track('view_subscription_products_list')
  }, [])

  if (isPending || isError) {
    return (
      <StyledContent>
        <CenterWrap>
          <Spinner />
        </CenterWrap>
      </StyledContent>
    )
  }

  return (
    <StyledContent id="infinity-scroll-target" isPullEvent={true} onPull={refetch} backgroundColor="gray_1">
      <ContentsContainer>
        <SortContainer>
          <Sort<GetProductSaleListParams['sort']> sortType={sort} sortList={SORT_LIST} setSortType={setSort} />
        </SortContainer>
        <InfinityScroll length={data.pages.length} isLast={!hasNextPage} next={fetchNextPage}>
          <ProductsContainer>
            {data?.pages.map((product) => (
              <ProductCard key={product.id} {...product} />
            ))}
          </ProductsContainer>
        </InfinityScroll>
      </ContentsContainer>
    </StyledContent>
  )
}

const StyledContent = styled(Content)`
  height: calc(100% - 86px);
`

const ContentsContainer = styled.div`
  padding: 0 16px 16px;
  height: auto;
`

const SortContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px;
`

const ProductsContainer = styled.article`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
